import React, { useEffect, useState } from 'react';
import {
  Button, Modal, Form, Input, InputNumber, Select, Space, message, Empty, Popconfirm, Tag, Grid,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { fiscalDataService } from '../../services/api';
import ResponsiveTable from '../../components/Common/ResponsiveTable';
import SATCatalogSearch from '../../components/Common/SATCatalogSearch';

const { useBreakpoint } = Grid;

/**
 * Catálogo de productos y servicios del contribuyente, mantenido por el gestor.
 *
 * Como los clientes: la cuenta gestionada es visor y no puede mantenerlo. Las
 * claves de producto y unidad se eligen del catálogo del SAT, que es lo que el
 * backend valida al guardar.
 */
const ProductosPanel = ({ empresa }) => {
  const screens = useBreakpoint();
  const esMovil = !screens.md;
  const companyId = empresa?.id;

  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [form] = Form.useForm();

  const cargar = async () => {
    if (!companyId) return;
    setCargando(true);
    try {
      const data = await fiscalDataService.getProducts(companyId);
      setLista(data?.productos || []);
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo cargar el catálogo');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const abrir = (registro) => {
    setEditando(registro || {});
    form.setFieldsValue(
      registro
        ? {
            name: registro.name,
            descripcion: registro.description,
            clave_producto_servicio: registro.claveprod,
            clave_unidad: registro.claveunidad,
            precio_unitario: registro.price,
            tipo: registro.type === 1 ? 'servicio' : 'producto',
          }
        : { tipo: 'servicio' }
    );
  };

  const guardar = async (values) => {
    setGuardando(true);
    try {
      if (editando?.id) {
        await fiscalDataService.updateProduct(companyId, editando.id, values);
        message.success('Producto actualizado');
      } else {
        await fiscalDataService.createProduct(companyId, values);
        message.success('Producto registrado');
      }
      setEditando(null);
      form.resetFields();
      await cargar();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (registro) => {
    try {
      await fiscalDataService.deleteProduct(companyId, registro.id);
      message.success('Producto eliminado');
      await cargar();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo eliminar');
    }
  };

  const columns = [
    { title: 'Nombre', dataIndex: 'name', key: 'name' },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      render: (t) => <Tag>{Number(t) === 1 ? 'Servicio' : 'Producto'}</Tag>,
    },
    { title: 'Clave SAT', dataIndex: 'claveprod', key: 'claveprod' },
    { title: 'Unidad', dataIndex: 'claveunidad', key: 'claveunidad' },
    {
      title: 'Precio',
      dataIndex: 'price',
      key: 'price',
      render: (p) =>
        new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(p || 0)),
    },
    {
      title: '',
      key: 'acciones',
      render: (_, fila) => (
        <Space wrap>
          <Button size="small" icon={<EditOutlined />} onClick={() => abrir(fila)}>
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar este producto?"
            okText="Eliminar"
            cancelText="Cancelar"
            onConfirm={() => eliminar(fila)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => abrir(null)}
        block={esMovil}
        size={esMovil ? 'large' : 'middle'}
        style={{ marginBottom: 12 }}
      >
        Registrar producto o servicio
      </Button>

      <ResponsiveTable
        rowKey="id"
        loading={cargando}
        dataSource={lista}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: true }}
        locale={{
          emptyText: <Empty description="Sin productos ni servicios" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
        }}
      />

      <Modal
        title={editando?.id ? `Editar — ${editando.name}` : 'Registrar producto o servicio'}
        open={editando !== null}
        onCancel={() => setEditando(null)}
        onOk={() => form.submit()}
        confirmLoading={guardando}
        okText="Guardar"
        cancelText="Cancelar"
        width={esMovil ? '100%' : 560}
        style={esMovil ? { top: 0, maxWidth: '100vw', margin: 0 } : undefined}
      >
        <Form form={form} layout="vertical" onFinish={guardar}>
          <Form.Item name="name" label="Nombre" rules={[{ required: true, message: 'Requerido' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="tipo" label="Tipo" rules={[{ required: true, message: 'Requerido' }]}>
            <Select
              options={[
                { value: 'servicio', label: 'Servicio' },
                { value: 'producto', label: 'Producto' },
              ]}
            />
          </Form.Item>
          {/* Las claves las valida el backend contra el catálogo del SAT: se eligen
              del buscador para no capturar una que será rechazada al guardar. */}
          <Form.Item
            name="clave_producto_servicio"
            label="Clave de producto o servicio (SAT)"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <SATCatalogSearch type="products" placeholder="Buscar clave del SAT" />
          </Form.Item>
          <Form.Item
            name="clave_unidad"
            label="Clave de unidad (SAT)"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <SATCatalogSearch type="units" placeholder="Buscar unidad" />
          </Form.Item>
          <Form.Item
            name="precio_unitario"
            label="Precio unitario"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ProductosPanel;
